import { useMemo } from "react";
import { ActivityIndicator, TouchableOpacity, View } from "react-native";

import { CalendarEvent, google, ics, outlook, yahoo } from "calendar-link";
import moment from "moment";

import { ChevronRight } from "@smartrent/icons";
import { Box, Button, Typography, useTheme } from "@smartrent/ui";

import { useApi } from "@/hooks/api";
import { ConfigType, TourType } from "@/types";

export function AddToCalendarModal({
  onClose,
  tour,
  config,
}: {
  onClose: () => void;
  tour: TourType;
  config: ConfigType;
}) {
  const { colors } = useTheme();

  const { response: groupResponse } = useApi({
    url: `/group/${config.groupId}`,
    trigger: [],
    forceDispatchEffect: () => !!config.groupId,
  });

  const services = ["apple", "google", "yahoo", "outlook"];

  const groupAddress: string = useMemo(() => {
    if (groupResponse) {
      const { data: group } = groupResponse;
      return `${group.street_address_1} ${group.street_address_2} ${group.city} ${group.state} ${group.zip}`;
    } else {
      return "";
    }
  }, [groupResponse]);

  const event: CalendarEvent = useMemo(() => {
    return {
      title: `Self Guided Tour at ${config.propertyName || ""}`,
      start: tour.scheduledStart
        ? moment(tour.scheduledStart).toISOString(true)
        : new Date(),
      end: tour.scheduledEnd
        ? moment(tour.scheduledEnd).toISOString(true)
        : new Date(),
      allDay: false,
      url: window.location.href,
      description: `Click the link below to view your tour details. \n\n<a href="${window.location.href.toString()}">${window.location.href.toString()}</a>`,
      location: groupAddress,
    };
  }, [
    config.propertyName,
    groupAddress,
    tour.scheduledEnd,
    tour.scheduledStart,
  ]);

  function handleDropdownLinkClick(type: string) {
    let url;

    switch (type) {
      case "google":
        url = google(event);
        break;
      case "yahoo":
        url = yahoo(event);
        break;
      case "outlook":
        url = outlook(event);
        break;
      default:
        url = ics({ ...event, description: "" });
        break;
    }

    if (type === "apple") {
      url = decodeURI(url.replace("data:text/calendar;charset=utf8,", ""));
      let blob = new Blob([url], {});
      let link = document.createElement("a");

      link.href = window.URL.createObjectURL(blob);
      link.download = "reminder.ics";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } else {
      window.open(url, "_blank");
    }

    onClose();
  }
  return (
    <Box
      style={{
        padding: 16,
      }}
    >
      <View style={{ marginVertical: 16 }}>
        <Typography type="bodyLarge" style={{ textAlign: "center" }}>
          Select a calendar
        </Typography>
      </View>

      {groupResponse ? (
        <>
          {services.map((service, index) => {
            return (
              <TouchableOpacity
                key={index}
                onPress={() => handleDropdownLinkClick(service)}
              >
                <View
                  style={{
                    alignItems: "center",
                    // if it's the last item, don't add a bottom border
                    borderBottomWidth: services.length - 1 === index ? 0 : 1,
                    borderBottomColor: colors.borderLow,
                    flexDirection: "row",
                    justifyContent: "space-between",
                    paddingVertical: 14,
                  }}
                >
                  <Typography>
                    {service.charAt(0).toUpperCase() + service.slice(1)}
                  </Typography>

                  <ChevronRight size={20} />
                </View>
              </TouchableOpacity>
            );
          })}
        </>
      ) : (
        <ActivityIndicator color={colors.primary} />
      )}

      <View style={{ marginVertical: 8 }}>
        <Button variation="plain" onPress={onClose} size="large">
          Cancel
        </Button>
      </View>
    </Box>
  );
}
